/**
 * Authentication Service
 * Handles MSAL authentication and token management
 */

import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { msalConfig, graphTokenRequest } from '@/config/msalConfig';
import type { User } from '@/types';

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
}

/**
 * Get the account that should drive auth/Graph calls.
 *
 * Prefers MSAL's own "active account" marker (set by `setActiveAccount` on
 * every successful login or silent SSO). Falls back to the first cached
 * account only when no active marker exists. The plain `getAllAccounts()[0]`
 * pattern is unsafe for users who have multiple Entra accounts in their
 * browser session — `getAllAccounts` order is non-deterministic, so it can
 * return a different account than the one the user just signed in with,
 * causing Graph calls (e.g. `/me/memberOf` for the group check) to query
 * the wrong account and trip the "Access denied" banner.
 */
export function getActiveAccount(): AccountInfo | null {
  return (
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0] ??
    null
  );
}

/**
 * Attempt silent SSO using the browser's existing Entra session cookie.
 * Works even when the MSAL local cache is empty (e.g. cleared storage).
 * Returns the account if successful, null if no active browser session exists.
 */
export async function trySilentSso(): Promise<AccountInfo | null> {
  try {
    const result = await msalInstance.ssoSilent({
      scopes: graphTokenRequest.scopes,
    });
    if (result.account) {
      msalInstance.setActiveAccount(result.account);
      return result.account;
    }
    return null;
  } catch {
    // No active Entra browser session — user must log in interactively
    return null;
  }
}

/**
 * Login with popup.
 *
 * By default the call is silent when the browser already has an Entra
 * session — single-account users are signed in with one click, no picker.
 * When `forceAccountPicker` is true we pass `prompt: 'select_account'` so
 * Microsoft ignores the ambient session and shows the account picker. This
 * is how the UI recovers from an ambient-session auto-pick landing on the
 * wrong account (e.g. Edge's personal MS account grabbed over a work one).
 */
export async function login(
  options?: { forceAccountPicker?: boolean }
): Promise<AuthenticationResult> {
  try {
    const response = await msalInstance.loginPopup({
      scopes: graphTokenRequest.scopes,
      ...(options?.forceAccountPicker ? { prompt: 'select_account' as const } : {}),
    });
    msalInstance.setActiveAccount(response.account);
    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Logout (local-only, no round-trip to Microsoft).
 *
 * Clears this app's MSAL cache and active account without calling AAD's
 * /logout endpoint. This is the pattern most enterprise SaaS apps use:
 * "signing out" means signing out of THIS app, not signing out of every
 * Microsoft app. It sidesteps three problems we hit with the popup logout:
 *
 *   1. Tenants that render a "You signed out of your account" branded page
 *      with no auto-redirect — the popup just sits there because MSAL
 *      never sees `post_logout_redirect_uri` in the URL.
 *   2. Users with many Entra groups whose idToken is too long for AAD's
 *      ~2 KB logout URL limit (AADSTS90015).
 *   3. The generic "Pick an account to sign out of" confirmation page.
 *
 * The user's Entra browser session stays valid, so the next sign-in is
 * either a silent auto-login (single correct account) or transparently
 * retried with the account picker if the ambient account isn't authorized
 * (handled upstream in AuthContext.handleLogin).
 */
export async function logout(): Promise<void> {
  const account = getActiveAccount();
  if (!account) return;
  msalInstance.setActiveAccount(null);
  await msalInstance.clearCache({ account });
}

/**
 * Get access token for Microsoft Graph
 */
export async function getAccessToken(): Promise<string> {
  const account = getActiveAccount();

  if (!account) {
    throw new Error('No active account');
  }

  try {
    // Try silent token acquisition first
    const response = await msalInstance.acquireTokenSilent({
      scopes: graphTokenRequest.scopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive
    if (error instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup({
        scopes: graphTokenRequest.scopes,
        account,
      });
      return response.accessToken;
    }
    throw error;
  }
}

/**
 * Convert MSAL account to User type
 */
export function accountToUser(account: AccountInfo): User {
  return {
    id: account.homeAccountId,
    name: account.name || '',
    email: account.username,
    // Default role is 'user' - can be overridden by group mappings or first-user-admin logic
    role: 'user',
  };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getActiveAccount() !== null;
}

